import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, type OnInit, PLATFORM_ID, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { addDays, isAfter } from 'date-fns';
import {
  HOME_SCREEN_ICON_VERSION,
  HOME_SCREEN_INSTALL_VERSION_KEY,
} from '@app/features/home-screen-icon/domain/properties/home-screen-icon.props';
import { OneSignalService } from '@app/services/one-signal/one-signal';
import { StorageService } from '@app/services/storage/storage';

const DISMISSED_AT_KEY = 'ios_install_nudge_dismissed_at';
const DISMISS_DAYS = 7;

/**
 * Temporary: the install banner stays in the code but is held back while we verify on real devices
 * that the "old Home-Screen install" detection fires at all. Flip to `visible` to restore it.
 */
const INSTALL_BANNER_MODE: 'hidden' | 'visible' = 'visible';

/** Temporary stand-in for the re-install banner, so the detection is visible on a device without devtools. */
const REINSTALL_ALERT_MESSAGE = 'Please reinstall the website from the home screen!';

/** Carries the icon version, so a future artwork change can prompt the same player again. */
const REINSTALL_ALERT_SEEN_KEY = 'home_screen_icon_alert_seen_v';

/**
 * iOS Safari only supports web push from a Home-Screen-installed PWA (OneSignalService gates
 * canPromptForPush() on isIos() && isStandalone()) — this banner is what actually gets visitors
 * into that state, since nothing else in the app explains why push "doesn't work" on iOS.
 *
 * Dismissal is a snooze, not a permanent opt-out: the timestamp is re-checked on every init and
 * the banner returns after DISMISS_DAYS, because most users tap the cross before reading it.
 */
@Component({
  selector: 'bingo-ios-install-nudge',
  imports: [MatIcon],
  templateUrl: './ios-install-nudge.html',
  styleUrl: './ios-install-nudge.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IosInstallNudge implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly oneSignalService = inject(OneSignalService);
  private readonly storageService = inject(StorageService);

  protected readonly isVisible = signal(false);

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const isIosOutsideHomeScreen = this.oneSignalService.isIos() && !this.oneSignalService.isStandalone();

    this.isVisible.set(INSTALL_BANNER_MODE === 'visible' && isIosOutsideHomeScreen && this.isSnoozeExpired());
    this.warnAboutOldHomeScreenIcon();
  }

  dismiss(): void {
    this.isVisible.set(false);
    this.storageService.setLocalItem(DISMISSED_AT_KEY, (new Date).toISOString());
  }

  private isSnoozeExpired(): boolean {
    const dismissedAt = this.storageService.getLocalItem(DISMISSED_AT_KEY);

    if (dismissedAt === null || dismissedAt === '') {
      return true;
    }

    const dismissedDate = new Date(dismissedAt);

    if (Number.isNaN(dismissedDate.getTime())) {
      return true;
    }

    return isAfter(new Date, addDays(dismissedDate, DISMISS_DAYS));
  }

  /**
   * iOS never refreshes a Home-Screen icon after install, so a player who added the app before the
   * rebrand still launches it from the old artwork. A fresh install launches with the manifest's
   * `?hs=<version>` marker, which homeScreenInstallInitializer persists before any redirect can drop
   * it; a missing or older stored marker in standalone mode means the icon predates this version.
   */
  private warnAboutOldHomeScreenIcon(): void {
    const browserWindow = this.document.defaultView;
    const seenKey = `${REINSTALL_ALERT_SEEN_KEY}${HOME_SCREEN_ICON_VERSION}`;

    if (!browserWindow || !this.isOldHomeScreenInstall() || this.storageService.getLocalItem(seenKey)) {
      return;
    }

    browserWindow.alert(REINSTALL_ALERT_MESSAGE);

    /* Written after the alert: it blocks until the player taps OK, so this only runs once seen. */
    this.storageService.setLocalItem(seenKey, 'true');
  }

  private isOldHomeScreenInstall(): boolean {
    if (!this.oneSignalService.isIos() || !this.oneSignalService.isStandalone()) {
      return false;
    }

    return this.storageService.getLocalItem(HOME_SCREEN_INSTALL_VERSION_KEY) !== String(HOME_SCREEN_ICON_VERSION);
  }
}
